import { PageHeader } from "@/components/PageHeader";
import { WardMark } from "@/components/WardMark";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type Me, firstName, useActiveOrg, useApiQuery } from "@/lib/api";
import { ArrowRight, ArrowUpRight, Folder, KeyRound, Terminal, Users } from "lucide-react";
import { Link, useOutletContext } from "react-router";

export function DashboardRoute() {
  const me = useOutletContext<Me>();
  const activeOrg = useActiveOrg();
  const projects = useApiQuery(
    ["projects", activeOrg.org_id],
    (client) => client.projects.list(activeOrg.org_id),
    { suppressToast: true },
  );
  const items = projects.data?.projects;
  const count = items?.length;
  return (
    <div className="overview fade-up">
      <div className="eyebrow mb-3">Your workspace / {activeOrg.name}</div>
      <PageHeader
        title={`Welcome back, ${firstName(me)}.`}
        subtitle="Everything you need to keep your secrets in safe hands."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/projects">
              Open projects <ArrowRight size={15} />
            </Link>
          </Button>
        }
      />
      <div className="overview-layout">
        <section className="overview-projects panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Projects</p>
              <h2>
                {count === undefined
                  ? "Your projects"
                  : count === 0
                    ? "A home for your first secrets."
                    : `${count} project${count === 1 ? "" : "s"} in ${activeOrg.name}`}
              </h2>
            </div>
            {count !== undefined && <span className="count-badge">{count} total</span>}
          </div>
          {projects.error ? (
            <div role="alert" className="p-6">
              <p>Couldn't load your projects.</p>
              <Button variant="outline" size="sm" onClick={projects.refetch} className="mt-3">
                Retry
              </Button>
            </div>
          ) : items === undefined ? (
            <div className="p-6 space-y-3" aria-label="Loading projects">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="overview-empty">
              <Folder size={32} strokeWidth={1.3} />
              <p>
                Group your app's environments and secrets in a project. Start with one for each
                service.
              </p>
              <Button asChild>
                <Link to="/projects">
                  Create your first project <ArrowRight size={15} />
                </Link>
              </Button>
            </div>
          ) : (
            <ul className="overview-project-list">
              {items.slice(0, 5).map((project) => (
                <li key={project.project_slug}>
                  <Link to={`/projects/${encodeURIComponent(project.project_slug)}`}>
                    <span className="project-icon">
                      <Folder size={20} strokeWidth={1.5} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong>{project.display_name || project.project_slug}</strong>
                      <code>{project.project_slug}</code>
                    </span>
                    <ArrowRight size={17} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {items && items.length > 5 && (
            <Link className="panel-footer-link" to="/projects">
              View all {items.length} projects <ArrowRight size={15} />
            </Link>
          )}
        </section>
        <aside className="workspace-note">
          <WardMark className="size-12 text-accent" />
          <p className="eyebrow">One workspace. Every environment.</p>
          <h2>
            From local dev
            <br />
            to production.
          </h2>
          <p>
            Keep secrets organized by project and environment. Choose who can access them, and how.
          </p>
          <a href="https://wardkey.app/docs/cli" target="_blank" rel="noreferrer">
            Explore the documentation <ArrowUpRight size={15} />
          </a>
        </aside>
      </div>
      <div className="section-heading">
        <h2>Make yourself at home</h2>
        <span>Tools for your workflow</span>
      </div>
      <div className="quick-links">
        <Link to="/settings/service-tokens">
          <KeyRound size={21} strokeWidth={1.5} />
          <h3>Connect your services</h3>
          <p>Manage tokens for scoped access from apps and automation.</p>
          <span>
            Service tokens <ArrowRight size={15} />
          </span>
        </Link>
        <Link to="/members">
          <Users size={21} strokeWidth={1.5} />
          <h3>Bring your team</h3>
          <p>Invite collaborators and manage workspace permissions.</p>
          <span>
            Manage members <ArrowRight size={15} />
          </span>
        </Link>
        <a href="https://wardkey.app/docs/cli/install" target="_blank" rel="noreferrer">
          <Terminal size={21} strokeWidth={1.5} />
          <h3>Meet your command line</h3>
          <p>Access your secrets without leaving your terminal.</p>
          <span>
            Install the CLI <ArrowUpRight size={15} />
          </span>
        </a>
      </div>
    </div>
  );
}
